import { type PointerEvent, useEffect, useRef, useState } from 'react';

/**
 * A signature drawn with a finger, a stylus or a mouse. It never leaves the page until the user
 * records it (a PNG sent once to the server, kept privately); it is not stored in the browser.
 */
export function SignaturePad({
  label,
  onChange,
}: {
  readonly label: string;
  /** The PNG (data URL) once something is drawn; null when cleared. */
  readonly onChange: (png: string | null) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<{ x: number; y: number } | null>(null);
  const [empty, setEmpty] = useState(true);
  const drawn = useRef(false);

  // Sharp lines on high-density screens, without huge images (at most twice the CSS size).
  useEffect(() => {
    const element = canvas.current;
    if (element === null) return;
    const scale = Math.min(2, window.devicePixelRatio || 1);
    const { width, height } = element.getBoundingClientRect();
    element.width = Math.max(300, Math.round(width * scale));
    element.height = Math.max(120, Math.round(height * scale));
    const context = element.getContext('2d');
    if (context === null) return;
    context.fillStyle = '#fff';
    context.fillRect(0, 0, element.width, element.height);
    context.lineWidth = 2.5 * scale;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111';
  }, []);

  const point = (event: PointerEvent<HTMLCanvasElement>) => {
    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * element.width) / rect.width,
      y: ((event.clientY - rect.top) * element.height) / rect.height,
    };
  };
  const finish = () => {
    if (drawing.current === null) return;
    drawing.current = null;
    const element = canvas.current;
    if (element !== null && drawn.current) onChange(element.toDataURL('image/png'));
  };
  const clear = () => {
    const element = canvas.current;
    const context = element?.getContext('2d');
    if (element == null || context == null) return;
    context.fillStyle = '#fff';
    context.fillRect(0, 0, element.width, element.height);
    drawn.current = false;
    setEmpty(true);
    onChange(null);
  };

  return (
    <div className="signature-pad">
      <canvas
        ref={canvas}
        className="signature-pad__canvas"
        role="img"
        aria-label={empty ? `${label}: ריק` : `${label}: נחתם`}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          drawing.current = point(event);
        }}
        onPointerMove={(event) => {
          const from = drawing.current;
          const context = event.currentTarget.getContext('2d');
          if (from === null || context === null) return;
          const to = point(event);
          context.beginPath();
          context.moveTo(from.x, from.y);
          context.lineTo(to.x, to.y);
          context.stroke();
          drawing.current = to;
          drawn.current = true;
          if (empty) setEmpty(false);
        }}
        onPointerUp={finish}
        onPointerCancel={finish}
      />
      <div className="signature-pad__bar">
        <span className="hint">{empty ? 'חתמו בתוך המסגרת' : 'אפשר לנקות ולחתום מחדש'}</span>
        <button type="button" className="button button--ghost" disabled={empty} onClick={clear}>
          ניקוי
        </button>
      </div>
    </div>
  );
}
